import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Record } from '@ltvco/refresh-lib/v1';
import { useContext, useEffect, useCallback, useRef } from 'react';

import { Message } from 'components/ChatBV/types';
import {
  normalizeSearchType,
  type ObituaryTeaserParamsForHandler,
  type ContactTeaserParamsForHandler,
  type AncestorSearchParams,
} from 'components/ChatBV/utils/conversationUtils';
import { getPublicRecordsTeaser } from 'components/ChatBV/utils/teaserUtils';

import { getObituarySearchParams } from './utils';

/** Normalize for teaser: undefined → undefined, else use shared normalizer from conversationUtils. */
function normalizeSearchTypeForTeaser(
  searchType: string | undefined
): string | undefined {
  return searchType !== undefined && searchType !== null && searchType !== ''
    ? normalizeSearchType(searchType)
    : undefined;
}

export interface TeaserLoaderSearchOptions {
  searchObituary?: (
    params: ObituaryTeaserParamsForHandler
  ) => Promise<{ results: unknown[]; count: number } | undefined>;
  searchAncestor?: (
    params: AncestorSearchParams
  ) => Promise<{ results: unknown[]; count: number } | undefined>;
  searchContact?: (
    params: ContactTeaserParamsForHandler
  ) => Promise<{ results: unknown[]; count: number }>;
  searchCourtCases?: (
    query: string
  ) => Promise<{ results: unknown[]; count: number }>;
  searchCourtOpinions?: (
    query: string
  ) => Promise<{ results: unknown[]; count: number }>;
}

interface UseTeaserLoaderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  /** Optional search functions to load teasers for obituary, ancestor, contact, court cases, court opinions from history */
  searchOptions?: TeaserLoaderSearchOptions;
}

function isCourtOpinionMessage(message: Message): boolean {
  return (
    message.content === 'COURT_OPINION_PAGE' || !!message.courtOpinionClusterId
  );
}

function isSameMessage(current: Message, original: Message): boolean {
  if (!current?.params && !original?.params) {
    return true;
  }
  if (!current?.params || !original?.params) {
    return false;
  }
  const hasName =
    current.params.first_name === original.params.first_name &&
    current.params.last_name === original.params.last_name;
  const hasCase =
    Boolean(current.params.case_name) &&
    Boolean(original.params.case_name) &&
    current.params.case_name === original.params.case_name;
  return hasName || hasCase;
}

export const useTeaserLoader = ({
  messages,
  setMessages,
  searchOptions = {},
}: UseTeaserLoaderProps) => {
  const { logError } = useContext(AppConfig);
  const loadingRef = useRef<Set<string>>(new Set());

  const loadTeaserForMessage = useCallback(
    async (message: Message, index: number) => {
      const messageKey = `teaser-${index}`;
      if (isCourtOpinionMessage(message)) {
        return;
      }
      if (
        loadingRef.current.has(messageKey) ||
        message.isLoadingTeaser !== true ||
        message.teaser
      ) {
        return;
      }

      const searchType = normalizeSearchTypeForTeaser(
        message.teaserSearchType || message.searchType
      );
      const teaserTypes = [
        'person',
        'obituary',
        'ancestor',
        'contact',
        'courtcases',
        'courtopinions',
      ];
      if (!searchType || !teaserTypes.includes(searchType)) {
        return;
      }

      const params = message.params;
      const hasNameParams = params?.first_name && params?.last_name;
      const hasCaseName = params?.case_name;
      const canLoadByName =
        hasNameParams &&
        ['person', 'obituary', 'ancestor', 'contact'].includes(searchType);
      const canLoadByCase =
        hasCaseName && ['courtcases', 'courtopinions'].includes(searchType);
      if (!canLoadByName && !canLoadByCase) {
        return;
      }

      loadingRef.current.add(messageKey);

      const updateMessage = (updates: Partial<Message>) => {
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[index];
          if (currentMessage && isSameMessage(currentMessage, message)) {
            updatedMessages[index] = { ...currentMessage, ...updates };
          }
          return updatedMessages;
        });
      };

      try {
        let teaserData: Record[] = [];

        if (searchType === 'person' || searchType === 'publicrecords') {
          if (!params?.first_name || !params?.last_name) {
            updateMessage({ isLoadingTeaser: false });
            return;
          }
          const teaserResult = await getPublicRecordsTeaser({
            first_name: params.first_name,
            last_name: params.last_name,
            middle_name: params.middle_name || '',
            age: params.age || '',
            state: params.state || '',
            city: params.city || '',
          });
          if (teaserResult?.teaserData?.length) {
            teaserData = teaserResult.teaserData as Record[];
          }
        } else if (searchType === 'obituary' && searchOptions.searchObituary) {
          const { name, dob, dod, location } = getObituarySearchParams(params);
          const response = await searchOptions.searchObituary({
            name,
            ...(dob && { dob }),
            ...(dod && { dod }),
            ...(location && { location }),
          });
          if (response?.results?.length) {
            teaserData = response.results as Record[];
          }
        } else if (searchType === 'ancestor' && searchOptions.searchAncestor) {
          if (!params?.first_name || !params?.last_name) {
            updateMessage({ isLoadingTeaser: false });
            return;
          }
          const response = await searchOptions.searchAncestor({
            first_name: params.first_name,
            last_name: params.last_name,
            middle_name: params.middle_name ?? '',
            age: params.age ?? '',
            state: params.state ?? '',
            city: params.city ?? '',
            birth_month: params.birth_month ?? '',
            birth_year: params.birth_year ?? '',
            death_month: params.death_month ?? '',
            death_year: params.death_year ?? '',
          });
          if (response?.results?.length) {
            teaserData = response.results as Record[];
          }
        } else if (searchType === 'contact' && searchOptions.searchContact) {
          const name = [
            params!.first_name,
            params!.middle_name,
            params!.last_name,
          ]
            .filter(Boolean)
            .join(' ')
            .trim();
          if (!name) {
            updateMessage({ isLoadingTeaser: false });
            return;
          }
          const response = await searchOptions.searchContact({
            name,
            title: params!.title ?? null,
            company: params!.company ?? null,
            city: params!.city ?? null,
            state: params!.state ?? null,
            industry: params!.industry ?? null,
            level: params!.level ?? null,
          });
          if (response?.results?.length) {
            teaserData = response.results as Record[];
          }
        } else if (
          (searchType === 'courtcases' || searchType === 'courtopinions') &&
          (searchOptions.searchCourtCases || searchOptions.searchCourtOpinions)
        ) {
          const query =
            params?.case_name?.trim() ||
            [params?.first_name, params?.middle_name, params?.last_name]
              .filter(Boolean)
              .join(' ')
              .trim();
          if (!query) {
            updateMessage({ isLoadingTeaser: false });
            return;
          }
          const loader =
            searchType === 'courtcases'
              ? searchOptions.searchCourtCases
              : searchOptions.searchCourtOpinions;
          if (loader) {
            const response = await loader(query);
            if (response?.results?.length) {
              teaserData = response.results as Record[];
            }
          }
        }

        updateMessage({
          teaser: teaserData.length > 0 ? teaserData : undefined,
          isLoadingTeaser: false,
        });
      } catch (error) {
        logError('Error loading teaser for message', error as Error);
        updateMessage({ isLoadingTeaser: false });
      } finally {
        loadingRef.current.delete(messageKey);
      }
    },
    [setMessages, logError, searchOptions]
  );

  useEffect(() => {
    messages.forEach((message, index) => {
      if (isCourtOpinionMessage(message)) {
        return;
      }
      if (
        message.isLoadingTeaser !== true ||
        message.teaser ||
        !message.params
      ) {
        return;
      }
      const searchType = normalizeSearchTypeForTeaser(
        message.teaserSearchType || message.searchType
      );
      const teaserTypes = [
        'person',
        'obituary',
        'ancestor',
        'contact',
        'courtcases',
        'courtopinions',
      ];
      if (!searchType || !teaserTypes.includes(searchType)) {
        return;
      }
      const hasName = message.params.first_name && message.params.last_name;
      const hasCase = message.params.case_name;
      const canLoad =
        (hasName &&
          ['person', 'obituary', 'ancestor', 'contact'].includes(searchType)) ||
        (hasCase && ['courtcases', 'courtopinions'].includes(searchType));
      if (!canLoad) {
        return;
      }
      const hasLoaderForType =
        searchType === 'person' ||
        (searchType === 'obituary' && searchOptions.searchObituary) ||
        (searchType === 'ancestor' && searchOptions.searchAncestor) ||
        (searchType === 'contact' && searchOptions.searchContact) ||
        (searchType === 'courtcases' && searchOptions.searchCourtCases) ||
        (searchType === 'courtopinions' && searchOptions.searchCourtOpinions);
      if (hasLoaderForType) {
        loadTeaserForMessage(message, index);
      }
    });
  }, [messages, loadTeaserForMessage, searchOptions]);
};
